import { useState, useEffect, useContext } from "react";
import { Box, Typography, IconButton, CircularProgress } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {ObservationContext} from './components/ObservationContext'
import VolumeBarChart from "./components/VolumeBarChart";
import formatDate from "../components/formatDate";
import TripleToggleSwitch from "./components/TripleToggleSwitch";
import DownloadObservationButton from "../components/DownloadObservationButton";

const ObservationLeftContainer = ({  leakSelected, observationData }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [leakDelta, setLeakDelta] = useState("Unknown");
  const { observationSelected, setObservationSelected } =
    useContext(ObservationContext);
  const [deltaPercentage, setDeltaPercentage] = useState("Unknown");
  const [switchChecked, setSwitchChecked] = useState("left");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    if (leakSelected && observationData && observationData.length > 0) {
      const delta = observationData[0].delta;
      if (observationData.length > 1) {
        const percentage = (delta / observationData[1].properties.volume) * 100;
        setLeakDelta(delta);
        setDeltaPercentage(percentage.toFixed(2));
      }
    }
    setIsLoading(false);
  }, [leakSelected, observationData]);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
    const observationRightContainer = document.querySelector(
      ".observation-right-container"
    );
    observationRightContainer.classList.toggle("collapsed");
  };

  const getBackgroundColor = (value) => {
    switch (value) {
      case 0: // HR
        return "#2b3b57";
      case 1: // VHR
        return "#d6ff59";
      default:
        return "#000000";
    }
  };

 const getTextColor = (value) => {
    switch (value) {
      case 0: // HR
        return "white";
      case 1: // VHR
        return "black";
      default:
        return "#000000";
    }
  };
  const handleSwitchChange = (value) => {
    setSwitchChecked(value);
    let resolutionModeValue = null;
    if (value === "center") resolutionModeValue = 0;
    else if (value === "right") resolutionModeValue = 1;
    const firstObservationOfType =
      resolutionModeValue !== null
        ? observationData.find(
            (observation) =>
              observation.properties.acquisition_info.sensor_resolution_mode ===
              resolutionModeValue
          )
        : null;
    if (firstObservationOfType) {
      setObservationSelected(firstObservationOfType);
    }
  };
  
  const getLeakData = (field) => {
    if (!leakSelected || Object.keys(leakSelected).length === 0) {
      return "No data";
    }
    return leakSelected[field] || "No data";
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={`observation-left-container ${collapsed ? "collapsed" : ""}`}>
      <Box className={`observation-container-main ${collapsed ? "collapsed" : ""}`}>
        <Box className="observation-container">
          <Box className="observation-leak-data">
            <Box className="observation-title-container">
              <Typography 
              component="h3" 
              className={`observation-title ${collapsed ? "collapsed" : ""}`}
              >
                {getLeakData('identifier')}
              </Typography>
              {!collapsed && (
                <Box className="observation-download-buttons">
                  <DownloadObservationButton dataSource={observationData} type="all" />
                </Box>
              )}
              <IconButton 
              className="collapse-button" 
              onClick={toggleCollapse}
              sx={{
                position:'absolute',
                right: '-13px',
                '&:hover': {
                  background: 'none',
                },
              }}
              >
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
            <Box className="observation-date-container-master">
              <Box className="observation-date-container">
                <Box>
                  <Typography className="observation-title-head">Start Date</Typography>
                  <Typography className={`observation-title-data ${collapsed ? "collapsed" : ""}`}>
                    {getLeakData('start_date') !== "No data" ? formatDate(getLeakData('start_date')) : "No data"}
                  </Typography>
                </Box>
                <Box>
                  <Typography className="observation-title-head">End Date</Typography>
                  <Typography className={`observation-title-data ${collapsed ? "collapsed" : ""}`}>
                    {getLeakData('end_date') !== "No data" 
                      ? formatDate(getLeakData('end_date'))
                      : getLeakData('end_date') === null ? "Currently Active" : "No data"}
                  </Typography>
                </Box>
              </Box>
              <Box className="observation-delta-container">
                <Typography className="observation-title-head">Delta </Typography>
                {leakDelta !== "No data" && (
                  <Typography className="observation-delta-text" style={{ color: leakDelta <= 0 ? "green" : "red" }}>
                    {leakDelta} barrels
                  </Typography>
                )}
                <Typography className="observation-delta-text" style={{ color: leakDelta <= 0 ? "green" : "red" }}>
                  {deltaPercentage !== "No data" ? `${deltaPercentage}%` : "No data"}
                </Typography>
              </Box>
            </Box>
            {!observationSelected && <Typography> Most Recent</Typography>}
          </Box>
        </Box>
        <Box className="observation-left-container-inner">
          <Box className="observation-title-head-container">
            <Box className="observation-title-switch">
              <Typography className="observation-title-head">Observations</Typography>
              <TripleToggleSwitch
                labels={{
                  left: { title: "Off", value: "Off" },
                  center: { title: "HR", value: "HR" },
                  right: { title: "VHR", value: "VHR" },
                }}
                onChange={handleSwitchChange}
              />
            </Box>
          </Box>
          <Box className="observation-left">
            {observationData && observationData.length > 0 ? (
              observationData.map((data) => {
                const shouldRender =
                  switchChecked === "left" ||
                  (switchChecked === "right" &&
                    data.properties.acquisition_info.sensor_resolution_mode ===
                      1) ||
                  (switchChecked === "center" &&
                    data.properties.acquisition_info.sensor_resolution_mode ===
                      0);

                return (
                  <Box
                    key={data.id}
                    className={`observation-card ${data.id === observationSelected.id ? "selected-card" : ""} 
                    ${switchChecked === "center" && data.properties.acquisition_info.sensor_resolution_mode === 1 ? "HR" : ""} 
                    ${switchChecked === "right" && data.properties.acquisition_info.sensor_resolution_mode === 0 ? "HR" : ""}`}
                    onClick={() => setObservationSelected(data)}
                  >
                    <Box className="observation-left-upper"></Box>
                    <Typography className="observation-detail-text">
                      {data.properties.identifier}
                    </Typography>
                    <Typography className="observation-detail-text-id">
                      {data.properties.acquisition_info
                        .sensor_resolution_mode === 1 &&
                        switchChecked === "center" &&
                        data.properties.identifier}
                      {data.properties.acquisition_info
                        .sensor_resolution_mode === 0 &&
                        switchChecked === "right" &&
                        data.properties.identifier}
                    </Typography>
                    {shouldRender && (
                      <Typography
                        className="observation-detail-text-label"
                        style={{
                          backgroundColor: getBackgroundColor(
                            data.properties.acquisition_info
                              .sensor_resolution_mode
                          ),
                          color: getTextColor(
                            data.properties.acquisition_info
                              .sensor_resolution_mode
                          )
                        }}
                      >
                        {data.properties.acquisition_info.sensor_resolution_mode
                          ? "VHR"
                          : "HR"}
                      </Typography>
                    )}
                  </Box>
                );
              })
            ) : (
              <Box className="no-results">No results</Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box className="observation-graphs">
        <VolumeBarChart dataSource={observationData} />
      </Box>
    </Box>
  );
};

export default ObservationLeftContainer;
